import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  SettingsGroupSettingPublicSchemaNullable,
  SettingsMergedSettingsSchemaNullable,
  SettingsSystemSettingPublicSchemaNullable,
  SettingsUserSettingSchemaNullable,
} from '@iconik-io/iconik-api/src/services/settings';

import type { SettingsState } from './types';

const initialState: SettingsState = {
  system: {},
  group: {},
  user: {},
  merged: {},
  loading: {
    system: false,
    group: false,
    user: false,
    merged: false,
  },
};

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    updateSystemSettings: (
      state,
      action: PayloadAction<{
        settings: SettingsSystemSettingPublicSchemaNullable;
      }>,
    ) => {
      state.system = {
        ...state.system,
        ...action.payload.settings,
      };
    },
    updateGroupSettings: (
      state,
      action: PayloadAction<{
        groupId: string;
        settings: SettingsGroupSettingPublicSchemaNullable;
      }>,
    ) => {
      state.group = {
        ...state.group,
        [action.payload.groupId]: {
          ...(state.group[action.payload.groupId] ?? {}),
          ...action.payload.settings,
        },
      };
    },
    updateUserSettings: (
      state,
      action: PayloadAction<{
        userId: string;
        settings: SettingsUserSettingSchemaNullable;
      }>,
    ) => {
      state.user = {
        ...state.user,
        [action.payload.userId]: {
          ...(state.user[action.payload.userId] ?? {}),
          ...action.payload.settings,
        },
      };
    },
    updateMergedSettings: (
      state,
      action: PayloadAction<{ settings: SettingsMergedSettingsSchemaNullable }>,
    ) => {
      state.merged = {
        ...state.merged,
        ...action.payload.settings,
      };
    },
    setSettingsLoading: (
      state,
      action: PayloadAction<{ type: string; loading: boolean }>,
    ) => {
      state.loading = {
        ...state.loading,
        [action.payload.type]: action.payload.loading,
      };
    },
  },
});

export const {
  updateSystemSettings,
  updateGroupSettings,
  updateUserSettings,
  updateMergedSettings,
  setSettingsLoading,
} = settingsSlice.actions;

export const settingsReducer = settingsSlice.reducer;
